<script lang="ts">
  import Modal from '@shared/ui/Modal.svelte';
  import type { SalesAction } from '@shared/types/sales-action';

  interface SolutionOption {
    id: string;
    label: string;
  }

  interface Props {
    open: boolean;
    action: SalesAction;
    solutions: SolutionOption[];
    /// `createContract` = true なら受注確定後に契約作成フローへ進む。
    onConfirm: (createContract: boolean, solutionId: string) => void;
    onClose: () => void;
    /// 作成成功後に linked_contract_id を保存できる親だけが true にする。
    showCreateContract?: boolean;
  }

  let {
    open,
    action,
    solutions,
    onConfirm,
    onClose,
    showCreateContract = false,
  }: Props = $props();

  // 受注は product_id 必須 (PATCH /status が won/lost で必須を強制)。 既存値を初期選択し、
  // 未設定行でもここで選ばせる。 ダイアログは対象ごとに再マウントされるため初期化で十分。
  let solutionId = $state(action.product_id ?? '');
  const canConfirm = $derived(solutionId !== '');
</script>

<Modal {open} title="受注として確定" onClose={onClose}>
  <div class="body">
    <p>
      <strong>{action.action_title ?? '(無題)'}</strong> を
      <strong>受注</strong>として確定します。このアクションは完了し、結果タブへ移動します。
    </p>
    <label>
      <span>ソリューション <em>*</em></span>
      <select bind:value={solutionId} data-testid="won-solution-select">
        <option value="">選択してください</option>
        {#each solutions as s (s.id)}
          <option value={s.id}>{s.label}</option>
        {/each}
      </select>
    </label>
    {#if canConfirm}
      {#if showCreateContract}
        <p class="muted">
          続けて契約を作成しますか?
          あとで作成を選んだ場合も、契約未作成として結果・契約タブに残ります。
        </p>
      {:else}
        <p class="muted">
          受注確定後、契約未作成として結果タブに残ります。
        </p>
      {/if}
    {:else}
      <p class="warn" data-testid="won-missing-solution">受注にはソリューションが必須です。</p>
    {/if}
  </div>
  {#snippet footer()}
    <button type="button" onclick={onClose}>キャンセル</button>
    <button
      type="button"
      onclick={() => onConfirm(false, solutionId)}
      disabled={!canConfirm}
    >
      あとで作成
    </button>
    {#if showCreateContract}
      <button
        type="button"
        class="primary"
        onclick={() => onConfirm(true, solutionId)}
        disabled={!canConfirm}
      >
        契約を作成する
      </button>
    {/if}
  {/snippet}
</Modal>

<style>
  .body {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .body p {
    margin: 0;
  }
  label {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  span em {
    color: #b91c1c;
    font-style: normal;
  }
  select {
    padding: 8px 10px;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    font: inherit;
    background: white;
  }
  .muted {
    color: #6b7280;
    font-size: 0.875rem;
  }
  .warn {
    color: #b45309;
    font-size: 0.875rem;
  }
  button {
    padding: 8px 16px;
    border: 1px solid #d1d5db;
    background: white;
    border-radius: 4px;
    cursor: pointer;
    font: inherit;
  }
  button.primary {
    background: #2563eb;
    border-color: #2563eb;
    color: white;
  }
  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
</style>
